/**
 * Take It Further Modal - Unified
 *
 * Supports both legacy (Observer) and new (other levels) block systems.
 * Legacy: Shows collect-only modal for Observer
 * New: Uses ActionModalBlock renderer for level-specific content
 * Explorer: Shows lens-aligned micro-challenges
 */

import React, { useEffect } from 'react';
import { Rocket, Layers } from 'lucide-react';
import CivicModal from './CivicModal';
import { ActionModalBlock } from '../lib/actionModalBlockTypes';
import { ActionModalBlockRenderer } from './ActionModalBlockRenderer';
import { useExplorer } from '../lib/ExplorerContext';
import { useAuth } from '../lib/AuthContext';

interface TakeItFurtherModalUnifiedProps {
  isOpen: boolean;
  onClose: () => void;
  system: 'legacy' | 'new';
  onSaveStory: () => void;
  isStorySaved: boolean;
  newBlocks?: ActionModalBlock[];
  civicLevel?: string;
  songId?: string;
}

export default function TakeItFurtherModalUnified({
  isOpen,
  onClose,
  system,
  onSaveStory,
  isStorySaved,
  newBlocks = [],
  civicLevel,
  songId,
}: TakeItFurtherModalUnifiedProps) {
  const { user } = useAuth();
  const { lensFocus, loadLensFocus } = useExplorer();

  const isExplorerLevel = civicLevel === 'Explorer';

  // Load lens focus for Explorer level
  useEffect(() => {
    if (isOpen && isExplorerLevel && songId && user) {
      loadLensFocus(songId);
    }
  }, [isOpen, isExplorerLevel, songId, user, loadLensFocus]);

  const getCategoryDisplayName = (category: string): string => {
    const map: Record<string, string> = {
      power: 'Power',
      justice: 'Justice',
      systems: 'Systems',
      identity: 'Identity',
      resistance: 'Resistance',
      institutions: 'Institutions',
      visibility: 'Visibility',
      collective_action: 'Collective Action',
      historical_patterns: 'Historical Patterns',
      structural_change: 'Structural Change',
    };
    return map[category] || category;
  };

  const renderLegacyContent = () => {
    return (
      <>
        <p
          className="text-[#A0A0A0] text-center mb-6"
          style={{
            fontSize: '14px',
            lineHeight: '1.5',
          }}
        >
          You don't need to act yet.
        </p>

        <div>
          <div className="mb-3">
            <h3
              className="font-semibold mb-3 text-center"
              style={{
                fontSize: '16px',
                color: '#40E0D0',
                letterSpacing: '0.01em',
              }}
            >
              Some stories don't change what we think. They change what we notice.
            </h3>
            <p
              className="text-[#E0E0E0] text-center"
              style={{
                fontSize: '14px',
                lineHeight: '1.65',
              }}
            >
              Meaning often shows up later. A song, a lyric, a feeling you can't shake.
              You don't have to name it. It's enough to let it stay with you.
            </p>
          </div>

          <div className="pt-4 flex flex-col items-center">
            <button
              onClick={onSaveStory}
              className="w-full sm:max-w-[420px] flex items-center justify-center gap-2.5 px-8 rounded-full border transition-all duration-200"
              style={{
                height: '48px',
                backgroundColor: isStorySaved ? 'rgba(64, 224, 208, 0.12)' : 'transparent',
                borderColor: isStorySaved ? 'rgba(64, 224, 208, 0.85)' : 'rgba(64, 224, 208, 0.40)',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                if (!isStorySaved) {
                  e.currentTarget.style.backgroundColor = 'rgba(64, 224, 208, 0.06)';
                  e.currentTarget.style.borderColor = 'rgba(64, 224, 208, 0.60)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isStorySaved) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(64, 224, 208, 0.40)';
                }
              }}
            >
              <Layers
                className="w-[16px] h-[16px]"
                stroke="#40E0D0"
                strokeWidth={1.5}
              />
              <span
                className="text-[15px] font-medium"
                style={{ color: '#40E0D0', letterSpacing: '0.005em' }}
              >
                {isStorySaved ? 'Collected' : 'Collect this story'}
              </span>
            </button>
          </div>
        </div>
      </>
    );
  };

  const renderNewBlocks = () => {
    if (newBlocks.length === 0) {
      return (
        <p className="text-[14px] text-[#E0E0E0]" style={{ lineHeight: '1.6' }}>
          No content available for this level yet.
        </p>
      );
    }

    return (
      <div>
        {newBlocks.map((block, index) => (
          <div key={block.id || index}>
            <ActionModalBlockRenderer block={block} />
          </div>
        ))}

        {/* Always include collect button for all levels */}
        <div className="pt-4 flex flex-col items-center mt-6 border-t border-[#282828]">
          <button
            onClick={onSaveStory}
            className="w-full sm:max-w-[420px] flex items-center justify-center gap-2.5 px-8 rounded-full border transition-all duration-200"
            style={{
              height: '48px',
              backgroundColor: isStorySaved ? 'rgba(64, 224, 208, 0.12)' : 'transparent',
              borderColor: isStorySaved ? 'rgba(64, 224, 208, 0.85)' : 'rgba(64, 224, 208, 0.40)',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              if (!isStorySaved) {
                e.currentTarget.style.backgroundColor = 'rgba(64, 224, 208, 0.06)';
                e.currentTarget.style.borderColor = 'rgba(64, 224, 208, 0.60)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isStorySaved) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(64, 224, 208, 0.40)';
              }
            }}
          >
            <Layers
              className="w-[16px] h-[16px]"
              stroke="#40E0D0"
              strokeWidth={1.5}
            />
            <span
              className="text-[15px] font-medium"
              style={{ color: '#40E0D0', letterSpacing: '0.005em' }}
            >
              {isStorySaved ? 'Collected' : 'Collect this story'}
            </span>
          </button>
        </div>
      </div>
    );
  };

  return (
    <CivicModal
      isOpen={isOpen}
      onClose={onClose}
      title="Take It Further"
      subtitle="A moment to pause and let it stay with you"
      icon={Rocket}
      maxWidth="md"
    >
      <div
        className="bg-[#1A1A1A] rounded-xl border"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.08)',
          padding: '32px 24px',
        }}
      >
        {/* Lens Callout Banner - Explorer Level Only */}
        {isExplorerLevel && lensFocus && system === 'new' && (
          <div
            className="mb-6 p-3 rounded-lg border border-[#40E0D0]/30 bg-[#40E0D0]/5"
          >
            <div className="flex items-start gap-2">
              <div className="mt-0.5">
                <Rocket className="w-4 h-4 text-[#40E0D0]" />
              </div>
              <div>
                <div className="text-[12px] font-semibold text-[#40E0D0] uppercase tracking-wide mb-1">
                  Lens: {getCategoryDisplayName(lensFocus.lens_category)}
                </div>
                <p className="text-[11px] text-[#B0B0B0] leading-relaxed">
                  Actions aligned with the {getCategoryDisplayName(lensFocus.lens_category).toLowerCase()} lens
                </p>
              </div>
            </div>
          </div>
        )}

        {system === 'legacy' ? renderLegacyContent() : renderNewBlocks()}
      </div>
    </CivicModal>
  );
}
